import React, { useState } from "react";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";

import ChartCard from "./ChartCard";
import ChartTooltip from "./ChartTooltip";
import {
  useChartTheme,
  formatCurrency,
  formatCompactCurrency,
  formatNumber,
  formatPeriod,
} from "./dashboardUtils";

const CHART_HEIGHT = 300;

const SalesTrendChart = ({ data, granularity }) => {
  const chartTheme = useChartTheme();
  const [metric, setMetric] = useState("revenue");

  const isEmpty = !data || data.every((d) => d.orders === 0);
  const axisProps = {
    stroke: chartTheme.grid,
    tick: { fill: chartTheme.muted, fontSize: 12 },
    tickLine: false,
  };
  const xAxis = (
    <XAxis
      dataKey="period"
      {...axisProps}
      tickFormatter={(p) => formatPeriod(p, granularity)}
      minTickGap={16}
    />
  );
  const tooltip = (formatter) => (
    <Tooltip
      cursor={{ stroke: chartTheme.muted, fill: chartTheme.grid }}
      content={
        <ChartTooltip
          labelFormatter={(p) => formatPeriod(p, granularity, { long: true })}
          formatter={formatter}
        />
      }
    />
  );
  const grid = <CartesianGrid vertical={false} stroke={chartTheme.grid} />;

  // Booked and collected share a unit, so they share one axis. Order counts
  // are a different unit and get their own view rather than a second axis.
  const chart = (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      {metric === "revenue" ? (
        <LineChart
          data={data}
          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
        >
          {grid}
          {xAxis}
          <YAxis
            {...axisProps}
            axisLine={false}
            width={64}
            tickFormatter={formatCompactCurrency}
          />
          {tooltip(formatCurrency)}
          <Legend
            iconType="plainline"
            wrapperStyle={{ fontSize: 13 }}
            // Legend text stays in text ink; the icon carries the series colour.
            formatter={(value) => (
              <span style={{ color: chartTheme.text }}>{value}</span>
            )}
          />
          <Line
            type="monotone"
            dataKey="booked"
            name="Booked"
            stroke={chartTheme.series[0]}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: chartTheme.surface }}
          />
          <Line
            type="monotone"
            dataKey="collected"
            name="Collected (net of refunds)"
            stroke={chartTheme.series[1]}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: chartTheme.surface }}
          />
        </LineChart>
      ) : (
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          {grid}
          {xAxis}
          <YAxis
            {...axisProps}
            axisLine={false}
            width={40}
            allowDecimals={false}
          />
          {tooltip(formatNumber)}
          <Bar
            dataKey="orders"
            name="Orders"
            fill={chartTheme.series[0]}
            radius={[4, 4, 0, 0]}
            maxBarSize={32}
          />
        </BarChart>
      )}
    </ResponsiveContainer>
  );

  const table = (
    <TableContainer style={{ maxHeight: CHART_HEIGHT + 40 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Period</TableCell>
            <TableCell align="right">Orders</TableCell>
            <TableCell align="right">Booked</TableCell>
            <TableCell align="right">Collected</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(data || []).map((row) => (
            <TableRow key={row.period}>
              <TableCell>
                {formatPeriod(row.period, granularity, { long: true })}
              </TableCell>
              <TableCell align="right">{formatNumber(row.orders)}</TableCell>
              <TableCell align="right">{formatCurrency(row.booked)}</TableCell>
              <TableCell align="right">
                {formatCurrency(row.collected)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <ChartCard
      title="Sales over time"
      subtitle={`By order date, grouped by ${granularity}. Cancelled orders excluded.`}
      isEmpty={isEmpty}
      actions={
        <ToggleButtonGroup
          size="small"
          exclusive
          value={metric}
          onChange={(e, value) => value && setMetric(value)}
          aria-label="Sales metric"
        >
          <ToggleButton value="revenue">Revenue</ToggleButton>
          <ToggleButton value="orders">Orders</ToggleButton>
        </ToggleButtonGroup>
      }
      chart={chart}
      table={table}
    />
  );
};

export default SalesTrendChart;
